<template>
  <div v-click-outside="onTableOutClick" class="sfdc-table">
    <Table
      :default-columns="sfdcTableColumns"
      :row-data="sfdcTableValues"
      :selected-rows="selectedRowByIds"
      :table-action-columns="tableActionColumns"
      :active-row="activeRow"
      primary-key="invoiceNumber"
      cell-background="#fff"
      header-cell-background="#f7f7f7"
      no-data-message="No invoice found"
      has-select-checkbox
      @checkIsDisabled="checkIsDisabled"
      @selectChangeEvent="selectChangeEvent"
      @selectAllEvent="selectChangeEvent"
      @addParentClass="addParentClass"
      @doubleClickCallback="doubleClickCallback">
      <template #default="{ row, col }">
        <span
          v-if="col.slotKey === 'invoiceNumber'"
          :class="
            isInvoiceRestrictedToEdit(row)
              ? 'sfdc-table__primary-key sfdc-table__cancelled-invoice'
              : 'sfdc-table__primary-key'
          ">
          {{ row[col.slotKey] }}
        </span>
        <span v-else-if="col.slotKey === 'reasonDescription'">
          <Select
            id="selectInput"
            :value="getDisputeReason(row[col.slotKey])"
            :options="disputeReasons"
            :error="disputeReasonValidation(row[col.slotKey])"
            :error-message="disputeReasonErrorMessage(row, col)"
            @input="onSelectDisputeReason($event, row)" />
        </span>
        <span
          v-else-if="col.slotKey === 'statusCodeDesc' && row.ohpDisputeId"
          class="sfdc-table__stage">
          <span
            v-tooltip="{ content: row[col.slotKey], classes: 'sfdcToolTip' }"
            class="sfdc-table__stage__status-desc">
            {{ row[col.slotKey] || "-" }}
          </span>
          <span>
            <span class="sfdc-table__stage__case-id">Dispute id: </span>
            <a
              :href="`/dispute-case-details/${row.ohpDisputeId}`"
              target="_blank"
              rel="noopener noreferrer">
              <span class="sfdc-table__stage__dispute-id">{{
                row.ohpDisputeId
              }}</span>
            </a>
          </span>
        </span>
        <span
          v-else-if="col.slotKey === 'formValidation'"
          v-tooltip.left="{
            content: sfdcTooltip(
              isInvoiceRestrictedToEdit(row)
                ? getValidationMsgForRestrictedInvoices(row)
                : 'Validation Successful',
              isInvoiceRestrictedToEdit(row)
                ? []
                : validateSfdcRow(row, col, 'tooltip', { disputeReasons })
            ),
            html: true,
            classes: 'sfdcToolTip',
          }"
          class="sfdc-table__validation">
          <icon
            :name="
              validateSfdcRow(row, col, 'icon', { disputeReasons }) ||
              isInvoiceRestrictedToEdit(row)
                ? 'exclamation-octagon'
                : 'check-circle'
            "
            class="sfdc-table__validation__icon"
            :class="
              isInvoiceRestrictedToEdit(row) ||
              validateSfdcRow(row, col, '', { disputeReasons })
                ? 'error'
                : 'success'
            " />
          <span
            class="sfdc-table__validation"
            :class="
              isInvoiceRestrictedToEdit(row) ||
              validateSfdcRow(row, col, '', { disputeReasons })
                ? 'seperator error'
                : ''
            ">
            {{
              isInvoiceRestrictedToEdit(row)
                ? getValidationMsgForRestrictedInvoices(row)
                : validateSfdcRow(row, col, "validateText", { disputeReasons })
            }}
          </span>
        </span>
        <span
          v-else-if="col.slotKey === 'action'"
          class="sfdc-table__validation">
          <span
            v-if="!row.loading"
            class="sfdc-table__validation__button-wrapper">
            <Button
              :label="invoiceButtonName(row)"
              appearance="tertiary"
              :disabled="
                validateSfdcRow(row, validationColumns, '', {
                  disputeReasons,
                }) ||
                row.statusCode === '10' ||
                isInvoiceRestrictedToEdit(row)
              "
              @onClick="onUpdateVoice(row)" />
            <icon
              v-if="row.error"
              v-tooltip="{
                content: `<span class='sfdc-tootip'><span class='error error-icon-message'>${row.errorMessage}</span></span>`,
                html: true,
                classes: 'sfdcToolTip',
              }"
              name="exclamation-octagon"
              class="sfdc-table__validation__icon error" />
          </span>
          <span v-else class="sfdc-table__validation__loader-wrapper">
            <Button
              class="sfdc-table__validation__loader-wrapper__loader-button"
              appearance="tertiary"
              :loading="row.loading" />
            {{ invoiceActionState(row) }}
          </span>
        </span>
        <span
          v-else
          v-tooltip.bottom="{
            content: sfdcTooltip(row[col.slotKey] || col.placeholderText),
            html: true,
            classes: 'sfdcToolTip errorIcon',
          }"
          :class="
            validateSfdcRow(row, col, '', { disputeReasons }) ? 'isError' : ''
          "
          @click.self="onCellEdit(row, col)">
          {{ row[col.slotKey] || col.placeholderText || "-" }}
        </span>
      </template>
      <template #editRow="{ row, col }">
        <edit-component
          :row="row"
          :col="col"
          :dispute-reasons="disputeReasons"
          v-on="$listeners" />
      </template>
    </Table>
    <dispute-creation-confirmation-modal
      v-if="isVisible"
      :row-data="rowData"
      :button-name="buttonName"
      @onModalClose="onModalClose"
      @updateInvoice="updateInvoiceData" />
  </div>
</template>

<script>
import EditComponent from "@/components/Sfdc/EditComponent";
import { validateSfdcRow, isEmpty } from "@/utils/helpers.js";
import {
  sfdcValidationTypes,
  sfdcTooltip,
  closedDisputeStatusCodes,
} from "@/config/sfdcConstant.js";
// Directives
import ClickOutside from "vue-click-outside";
import DisputeCreationConfirmationModal from "../DisputeCreationConfirmationModal/DisputeCreationConfirmationModal.vue";
export default {
  name: "SfdcTable",
  components: {
    EditComponent,
    DisputeCreationConfirmationModal,
  },
  directives: {
    ClickOutside,
  },
  props: {
    activeRow: { type: Object, default: () => {} },
    sfdcTableColumns: { type: Array, default: () => [] },
    sfdcTableValues: { type: Array, default: () => [] },
    tableActionColumns: { type: Array, default: () => [] },
    disputeReasons: { type: Array, default: () => [] },
    selectedRows: { type: Array, default: () => [] },
    selectedRowByIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      sfdcValidationTypes,
      validateSfdcRow,
      isEmpty,
      sfdcTooltip,
      invoiceError: {},
      validationColumns: {
        validateColumns: [
          "reasonDescription",
          "sfdcCaseId",
          "noteDescription",
          "contactEmail",
          "contactPerson",
        ],
      },
      isVisible: false,
      rowData: {},
      buttonName: "",
    };
  },
  methods: {
    onModalClose() {
      this.isVisible = false;
      this.rowData = {};
    },
    isInvoiceRestrictedToEdit(row) {
      return (
        !isEmpty(row.cancelledInvoiceNo) ||
        row.billingType === "Z029" ||
        row.billingType === "Z005" ||
        row.billingType === "Z020" ||
        row.billingType === "Z007" ||
        row.billingType === "CF" ||
        row.billingType === "CD" ||
        row.billingType === "VS" ||
        row.billingType === "CM" ||
        row.CompanyCode === "3000"
      );
    },

    getValidationMsgForRestrictedInvoices(row) {
      if (!isEmpty(row.cancelledInvoiceNo)) {
        return `Cancelled Invoice`;
      } else if (row.CompanyCode === "3000") {
        return `Company Code ${row.CompanyCode}`;
      } else {
        return `Billing Type ${row.billingType}`;
      }
    },
    getDisputeReason(value) {
      const findIndex = this.disputeReasons.findIndex(
        (item) => item?.value?.toLowerCase() === value?.toLowerCase()
      );
      if (findIndex > -1) {
        return this.disputeReasons[findIndex]?.value;
      }
      return "";
    },
    addParentClass(val) {
      this.tableParentClass = val;
    },
    disputeReasonValidation(value) {
      const index = this.disputeReasons.findIndex(
        (item) => item.value?.toLowerCase() === value?.toLowerCase()
      );
      const isError = index > -1 ? false : true;
      return isError;
    },
    disputeReasonErrorMessage(row, col) {
      if (this.activeRow?.["invoiceNumber"] === row["invoiceNumber"]) {
        const slotKey = col.slotKey;
        const index = this.disputeReasons.findIndex(
          (item) => item.value?.toLowerCase() === row[slotKey]?.toLowerCase()
        );
        return index > -1 ? "" : col[slotKey]?.errorMessage;
      } else {
        return "";
      }
    },
    onSelectDisputeReason(selectedValue, row) {
      this.$emit("onSelectDisputeReason", selectedValue, row);
    },
    doubleClickCallback(row) {
      this.$emit("updateActiveRow", row);
    },
    onCellEdit(row, col) {
      if (
        col?.editableField &&
        ((this.activeRow &&
          this.activeRow["invoiceNumber"] !== row["invoiceNumber"]) ||
          isEmpty(this.activeRow))
      ) {
        this.doubleClickCallback(row);
      }
    },
    onUpdateVoice(row) {
      if (row.invoiceAmount < 0) {
        this.rowData = row;
        this.buttonName = this.invoiceButtonName(row);
        this.isVisible = true;
      } else {
        this.updateInvoiceData(row);
      }
    },
    updateInvoiceData(row) {
      this.$emit("updateInvoice", row);
    },
    selectChangeEvent(selectedItems) {
      this.$emit("onMultiSelectInvoice", selectedItems);
    },
    checkIsDisabled(row) {
      return (
        validateSfdcRow(row, this.validationColumns, "", {
          disputeReasons: this.disputeReasons,
        }) ||
        row.statusCode === "10" ||
        row.invoiceAmount < 0 ||
        row.CompanyCode === "3000" ||
        this.isInvoiceRestrictedToEdit(row)
      );
    },
    invoiceButtonName(row) {
      return row?.statusCode
        ? closedDisputeStatusCodes.includes(row?.statusCode)
          ? "Re-dispute"
          : "Update dispute"
        : "Create dispute";
    },
    invoiceActionState(row) {
      return row?.disputeStage && row?.statusCode
        ? "Updating the dispute..."
        : "Creating a new dispute...";
    },
    onTableOutClick({ path }) {
      if (!isEmpty(path)) {
        const isInsideTable = path.some(
          (item) => item.className === "sfdc-table"
        );
        if (!isInsideTable) this.$emit("updateActiveRow", {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sfdc-table {
  overflow-y: auto;
  border: 1px solid var(--Gray-700);
  &__primary-key {
    font-family: "Maersk Text";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.5px;
    color: #282828;
  }
  &__cancelled-invoice {
    color: var(--Danger);
  }
  .isError {
    font-family: "Maersk Text";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: var(--Danger);
  }
  .error {
    color: var(--Danger);
  }
  .success {
    color: var(--Success);
  }
  &__validation {
    display: flex;
    cursor: pointer;
    &__button-wrapper {
      display: flex;

      i {
        cursor: pointer;
        margin-left: 10px;
      }
    }
    &__icon {
      display: flex;
      align-items: center;
      margin-right: 4px;
      &__error {
        color: var(--Danger);
      }
    }
    &__loader-wrapper {
      display: flex;
      flex-direction: column;
      &__loader-button {
        border: 0;
        background: transparent;
        width: 0;
        margin: 0 auto;
        pointer-events: none;
        &:after {
          border-top-color: var(--Blue-300-Maersk) !important;
        }
      }
    }
    .seperator {
      text-decoration: underline;
    }
  }
  &__stage {
    display: flex;
    flex-direction: column;
    &__status-desc {
      cursor: text;
      width: min-content;
    }
    &__case-id {
      font-family: "Maersk Text";
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      color: var(--Gray-600);
    }
    &__status-desc {
      cursor: text;
      width: min-content;
    }
    &__dispute-id {
      font-family: "Maersk Text";
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      color: var(--Blue-600-Dark);
      text-decoration: underline;
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
.sfdc-table {
  .table-flex__row--header {
    color: #003e5e;
    border-bottom: 2px solid #003e5e;
  }
}
.tooltip {
  &.sfdcToolTip {
    .tooltip-inner {
      background: var(--Gray-50);
      border: 1px solid var(--Gray-300);
      width: max-content;
      max-width: 350px;
      height: max-content;
      padding: 16px 8px;
      color: var(--Gray-700);
      .sfdc-tootip {
        ul {
          margin: 0;
          padding: 0 30px;
        }
        &__content {
          font-family: "Maersk Text";
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          color: var(--Gray-700);
          margin: 4px 0;
        }
        .error {
          color: var(--Danger);
        }
        .error-icon-message {
          font-family: "Maersk Text";
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
        }
      }
    }
  }
  .tooltip-arrow {
    border-color: var(--Gray-200);
  }
}
</style>
